// modal-container.component.ts
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  QueryList,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import {AsyncPipe, CommonModule, NgComponentOutlet, NgForOf} from '@angular/common';
import {ModalConfig, ModalService} from '../../services/modal.service';
import {Observable} from 'rxjs';
import {animate, style, transition, trigger} from '@angular/animations';
import {delay, tap} from 'rxjs/operators';
import {SnackbarConfig, SnackbarService} from '../../services/snackbar.service';
import {TranslateService} from '../../services/translate.service';
import {SmallActionButtonComponent} from '../buttons/small-action-button.component';
import {CircleButtonComponent} from '../buttons/circle-button.component';

@Component({
  selector: 'app-modal-container',
  standalone: true,
  imports: [CommonModule, SmallActionButtonComponent, CircleButtonComponent],
  template: `
    <ng-container *ngFor="let modal of modals$ | async; trackBy: trackByFn; let i = index">
      <div class="fixed inset-0 z-[100] flex items-start justify-center pt-24 text-theme-text"
           [style.zIndex]="1000 + i"
           @modalAnimation>
        <div class="absolute inset-0 bg-black/50"
             (wheel)="$event.preventDefault();"
             (touchmove)="$event.preventDefault();"
             (click)="modal.closeOnBackdrop && closeModal()"></div>
        <div class="relative">
          <div #modalHost></div>
        </div>
      </div>
    </ng-container>

    <div *ngIf="snackbar$ | async as snackbar"
         class="fixed left-0 right-0 bottom-8 max-w-lg mx-auto px-8 z-[1000]"
         @snackbarAnimation>
      <div class="bg-theme-surface2 text-theme-text px-6 py-6 text-lg rounded-xl shadow-lg flex gap-4 border-2 border-theme-primary items-center">
        <span class="flex-1">{{ getString(snackbar.message) }}</span>
        <app-circle-button [icon]="'fa-close'" size="10" type="solid" fontSize="lg" (onClick)="dismissSnackbar()"></app-circle-button>

<!--        <app-small-action-button *ngIf="snackbar.action" [text]="snackbar.action" (onClick)="dismissSnackbar()"></app-small-action-button>-->
<!--        <button *ngIf="snackbar.action"-->
<!--                class="px-4 py-2 border rounded-xl hover:bg-gray-50"-->
<!--                (click)="dismissSnackbar()">-->
<!--          {{ snackbar.action }}-->
<!--        </button>-->
      </div>
    </div>
  `,
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('250ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('250ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('snackbarAnimation', [
      transition(':enter', [
        style({ bottom: '-100%' }),
        animate('250ms ease-out', style({ bottom: '2rem' })) // 40 in rem
      ]),
      transition(':leave', [
        animate('250ms ease-in', style({ bottom: '-100%' }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalContainerComponent {
  @ViewChildren('modalHost', { read: ViewContainerRef })
  modalHosts!: QueryList<ViewContainerRef>;
  modals$!: Observable<ModalConfig[]>;
  snackbar$!: Observable<SnackbarConfig | null>;

  constructor(
    private modalService: ModalService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private injector: Injector
  ) {}

  getString(s: string) {
    return this.translate.getString(s);
  }

  dismissSnackbar() {
    this.snackbarService.dismiss();
  }

  ngOnInit() {
    //this.modals$ = this.modalService.modals$;
    this.modals$ = this.modalService.modals$.pipe(
      tap(modals => {
        console.log('Modal container received new stack:', modals);
      })
    );
    this.snackbar$ = this.snackbarService.observeSnackbar().pipe(
      tap(snackbar => {
        console.log('Snackbar container received:', snackbar);
      })
    );
  }

  ngAfterViewInit() {
    this.modals$.pipe(
      delay(0)  // Wait for view to be ready
    ).subscribe(modals => {
      this.modalHosts.forEach((host, index) => {
        if (modals[index]) {
          host.clear();
          const componentRef = host.createComponent(modals[index].component, {
            injector: this.createInjector(modals[index].data)
          });
        }
      });
    });

    let snackbarTimeout: any;
    this.snackbar$.pipe(
      delay(0)  // Wait for view to be ready
    ).subscribe(snackbar => {
      // Clear any existing timeout
      if (snackbarTimeout) {
        clearTimeout(snackbarTimeout);
      }

      if (snackbar) {
        snackbarTimeout = setTimeout(() => {
          this.dismissSnackbar();
        }, snackbar.duration || 3000);
      }
    });
  }

  trackByFn(index: number, item: ModalConfig) {
    return index;
  }

  createInjector(data: any): Injector {
    return Injector.create({
      providers: [
        { provide: 'MODAL_DATA', useValue: data }
      ],
      parent: this.injector
    });
  }

  closeModal() {
    this.modalService.close();
  }
}
